import type { Lead } from '@up2stay/up2promo-api-client';

import { escapeCsvCell } from '@/core/lib/csv-cell';
import {
  eventNamesFromLead,
  resolveEventDisplayName,
} from '@/core/lib/lead-event-names';

/**
 * Leads CSV contract v1 (CRM handoff).
 *
 * - Uses separate **First name** / **Last name** columns.
 * - **Address** is the dedicated postal/street field (not Notes/Message).
 * - **Job Title** is the Lead `jobTitle` field.
 * - **Notes** concatenates note bodies in list order with ` | `.
 * - **Events** are resolved from a caller-supplied event name map (not tags).
 * - Event IDs are omitted; assignee is a display name when a map is provided.
 * - Created At is local wall time (`TT.MM.JJJJ HH:MM` in de), matching the list.
 */

export const LEADS_CSV_DELIMITER = ';';

export const LEADS_CSV_HEADERS = [
  'First name',
  'Last name',
  'Email',
  'Phone',
  'Company',
  'Address',
  'Job Title',
  'Status',
  'Source',
  'Tags',
  'Events',
  'Assignee',
  'Created At',
  'Notes',
  'Message',
] as const;

export type LeadsCsvExportOptions = {
  /** Map Event id → display name for the Events column. */
  eventNameById?: ReadonlyMap<string, string> | Record<string, string>;
  /** Localized header row; falls back to English contract headers. */
  headers?: readonly string[];
  statusLabels?: Readonly<Record<string, string>>;
  sourceLabels?: Readonly<Record<string, string>>;
  assigneeNameById?: ReadonlyMap<string, string> | Record<string, string>;
  /** Label when assigneeUserId is null/empty (Niemand). */
  unassignedLabel?: string;
  locale?: string;
};

function lookupMappedName(
  id: string,
  map: ReadonlyMap<string, string> | Record<string, string> | undefined,
): string {
  if (!id || !map) return '';
  if (map instanceof Map) return map.get(id) ?? '';
  return (map as Record<string, string>)[id] ?? '';
}

function asNameMap(
  map: ReadonlyMap<string, string> | Record<string, string> | undefined,
): Map<string, string> {
  if (!map) return new Map();
  if (map instanceof Map) return map;
  return new Map(Object.entries(map));
}

function lookupLabel(
  value: string,
  labels: Readonly<Record<string, string>> | undefined,
): string {
  if (!value) return '';
  if (!labels) return value;
  return labels[value] ?? value;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** Local wall time as `TT.MM.JJJJ HH:MM` (de) or `DD/MM/YYYY HH:MM` (en). */
function formatCreatedAt(iso: string, locale?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = pad2(date.getDate());
  const month = pad2(date.getMonth() + 1);
  const year = String(date.getFullYear());
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  if (locale?.toLowerCase().startsWith('de')) {
    return `${day}.${month}.${year} ${time}`;
  }
  return `${day}/${month}/${year} ${time}`;
}

function flattenNotes(lead: Lead): string {
  return (lead.notes ?? [])
    .map((n) => String(n.content ?? '').trim())
    .filter(Boolean)
    .join(' | ');
}

function leadToCsvRow(
  lead: Lead,
  options: LeadsCsvExportOptions = {},
): string[] {
  const eventIds = lead.eventIds ?? [];
  const extraNames = eventNamesFromLead(lead);
  const eventNames = eventIds
    .map((id) =>
      resolveEventDisplayName(id, asNameMap(options.eventNameById), {
        storedName: extraNames[id] ?? extraNames[id.trim().toLowerCase()],
        fallbackToId: false,
      }),
    )
    .filter(Boolean);
  const assigneeId = lead.assigneeUserId?.trim() ?? '';
  const assigneeName = assigneeId
    ? lookupMappedName(assigneeId, options.assigneeNameById).trim() ||
      assigneeId
    : (options.unassignedLabel ?? '');

  return [
    lead.firstName ?? '',
    lead.lastName ?? '',
    lead.email ?? '',
    lead.phone ?? '',
    lead.companyName ?? '',
    lead.address ?? '',
    lead.jobTitle ?? '',
    lookupLabel(lead.status ?? '', options.statusLabels),
    lookupLabel(lead.source ?? '', options.sourceLabels),
    (lead.tags ?? []).join(', '),
    eventNames.join(', '),
    assigneeName,
    formatCreatedAt(lead.createdAt ?? '', options.locale),
    flattenNotes(lead),
    lead.message ?? '',
  ];
}

/** Build a BOM-prefixed CSV string for the given leads (filtered set). */
export function exportLeadsCsv(
  leads: Lead[],
  options: LeadsCsvExportOptions = {},
): string {
  const headers = options.headers ?? LEADS_CSV_HEADERS;
  const lines = [
    headers
      .map((h) => escapeCsvCell(h, LEADS_CSV_DELIMITER))
      .join(LEADS_CSV_DELIMITER),
    ...leads.map((lead) =>
      leadToCsvRow(lead, options)
        .map((cell) => escapeCsvCell(cell, LEADS_CSV_DELIMITER))
        .join(LEADS_CSV_DELIMITER),
    ),
  ];
  return `\uFEFF${lines.join('\n')}\n`;
}

/** `leads-{workspaceSlug}-{YYYY-MM-DD}.csv` */
export function leadsCsvFilename(
  workspaceSlug: string,
  date: Date = new Date(),
): string {
  const slug = workspaceSlug
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `leads-${slug || 'workspace'}-${y}-${m}-${d}.csv`;
}
